"use strict";
/**
 * Circular QR codes.
 *
 * A standard QR is a square matrix, so a circular one cannot simply be the code
 * cropped — that destroys it. Instead this builds a *supergrid*: a single
 * uniform module grid N wide, where N is the diagonal of the code plus its
 * quiet zone (see `supergridSize`). The real code sits as a centred square
 * inside it, pseudorandom modules fill the rest, and anything whose centre
 * falls outside the inscribed circle is dropped. Because everything shares one
 * grid, the filler is indistinguishable in scale and alignment from the code
 * and the whole thing reads as one circular object.
 *
 * The filler encodes nothing. It is decoration that happens to look like code —
 * only the centred square carries data.
 *
 * This style bypasses `qr-code-styling` entirely: it needs raw matrix access,
 * which that library does not expose. It is pure string generation over
 * `qrcode-generator`, so the same function runs in the browser preview and in
 * the API's download renderer with no DOM involved.
 *
 * Three constraints here are load-bearing, and violating any of them produces a
 * code that looks perfect and does not decode at all:
 *
 * 1. **Integer module geometry.** A fractional module size puts modules on
 *    non-integer boundaries; the rasterizer antialiases the seams and a
 *    decoder's binarizer reads thin light lines splitting them. Measured: a
 *    20px module decodes, 24.32px does not.
 * 2. **Neighbour-aware corner rounding.** Rounding each module in isolation
 *    leaves a light notch wherever two dark modules meet, fragmenting the
 *    finder patterns. Measured: radius 0 decodes, 0.15 already does not.
 *    Corners are therefore rounded only where no adjacent module is dark.
 * 3. **A clear ring around the real code.** With filler butted directly against
 *    it, both jsQR and zxing fail. One module was the measured floor; the
 *    ISO-recommended four is what ships, and the grid is sized from it so it
 *    can be changed without the style degrading.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.renderCircularQRCodeSVG = exports.buildCircularGrid = exports.supergridSize = exports.CIRCULAR_CORNER_RADIUS = exports.CIRCULAR_FILL_DENSITY = void 0;
const qrcode_generator_1 = __importDefault(require("qrcode-generator"));
const qr_code_style_1 = require("./qr-code-style");
/**
 * Escape a value interpolated into an SVG attribute.
 *
 * Colours reaching here have already been through `sanitizeQRStyleConfig`,
 * which enforces 6-digit hex — but this renderer is the one place in the QR
 * path that builds markup from style values directly, and it is reachable from
 * anywhere holding a `QRStyleConfig`. Escaping here means the renderer does not
 * depend on a caller having sanitized first.
 */
function escapeAttr(value) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}
/** Proportion of filler cells that are dark. Tuned to match the code's own density. */
exports.CIRCULAR_FILL_DENSITY = 0.55;
/** Module corner radius as a fraction of module size. */
exports.CIRCULAR_CORNER_RADIUS = 0.35;
/**
 * Highest error correction. The filler crowds the code and these get printed
 * small, so the extra redundancy is worth the denser matrix.
 */
const CIRCULAR_ERROR_CORRECTION = 'H';
/**
 * Deterministic PRNG. The filler must be identical for a given code every time
 * it renders, or the preview and the downloaded file would differ — and a
 * customer re-downloading would get a visibly different image.
 */
function createRandom(seed) {
    let state = seed >>> 0;
    return () => {
        state = (state * 1664525 + 1013904223) >>> 0;
        return state / 0x100000000;
    };
}
/** Stable seed from the payload, so a given code always looks the same. */
function seedFromData(data) {
    let hash = 2166136261;
    for (let i = 0; i < data.length; i++) {
        hash ^= data.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
}
/**
 * Width of the supergrid in modules.
 *
 * The circle inscribed in the grid has to leave room for the code, its quiet
 * zone, *and* a ring of filler outside them — so the grid is the diagonal of
 * the protected square (code + quiet zone), not of the bare code. The extra
 * module widens that square before the diagonal is taken, buying roughly 0.7
 * modules of radial clearance so its corners sit inside the circle rather than
 * on it.
 *
 * Sizing from the code alone starved the filler. The quiet ring is enforced
 * unconditionally, before the circle test, so the code always got its full
 * clearance either way — measured identical under both sizings. What changed
 * was how much room was left for filler between the protected square and the
 * circle, and widening the quiet zone consumed it. Dark filler modules for
 * `https://cqr.example/abcd` (a 29-module code), under each sizing:
 *
 *   quiet:        0     1     2     4     8
 *   old:        284   229   170    68     0
 *   new:        284   374   383   520   775
 *
 * The old series collapses to zero, at which point the circular style silently
 * becomes a plain square code. Deriving the size from the quiet zone inverts
 * that — the grid grows with the value, so the filler survives and the quiet
 * zone is freely tunable.
 *
 * Parity is matched to the code so it centres on an exact module boundary
 * rather than a half one.
 */
function supergridSize(codeSize, quietModules) {
    const protectedSquare = codeSize + 2 * quietModules + 1;
    let size = Math.ceil(protectedSquare * Math.SQRT2);
    if ((size - codeSize) % 2 !== 0)
        size += 1;
    return size;
}
exports.supergridSize = supergridSize;
/**
 * @param requestedQuietModules clamped into the supported range rather than
 *   trusted. This is an exported entry point, so a caller reaching it directly
 *   — an admin script, another SDK consumer — bypasses `resolveQuietModules`
 *   and the sanitizer. A zero here would butt filler straight against the code
 *   and produce an SVG no scanner can read, with nothing raising an error, so
 *   the guarantee belongs where the grid is built rather than only at the
 *   callers that happen to go through the style path.
 */
function buildCircularGrid(data, requestedQuietModules = qr_code_style_1.CIRCULAR_QUIET_MODULES) {
    /* NaN has to be excluded before clamping: Math.round, Math.max and Math.min
       all propagate it, so it would reach supergridSize, make the grid size NaN
       and produce a silently empty grid — no rows, no error. It is also the most
       likely bad input here, since it is what arithmetic on a parsed value
       yields. */
    const quietModules = Number.isFinite(requestedQuietModules)
        ? Math.min(qr_code_style_1.MAX_CIRCULAR_QUIET_MODULES, Math.max(qr_code_style_1.MIN_CIRCULAR_QUIET_MODULES, Math.round(requestedQuietModules)))
        : qr_code_style_1.CIRCULAR_QUIET_MODULES;
    const qr = (0, qrcode_generator_1.default)(0, CIRCULAR_ERROR_CORRECTION);
    qr.addData(data);
    qr.make();
    const codeSize = qr.getModuleCount();
    const size = supergridSize(codeSize, quietModules);
    const offset = (size - codeSize) / 2;
    const random = createRandom(seedFromData(data));
    const centre = (size - 1) / 2;
    const radius = size / 2;
    const cells = [];
    for (let row = 0; row < size; row++) {
        cells[row] = [];
        for (let col = 0; col < size; col++) {
            const insideCode = row >= offset &&
                row < offset + codeSize &&
                col >= offset &&
                col < offset + codeSize;
            if (insideCode) {
                cells[row][col] = qr.isDark(row - offset, col - offset) ? 'real' : 'empty';
                continue;
            }
            const insideQuietRing = row >= offset - quietModules &&
                row < offset + codeSize + quietModules &&
                col >= offset - quietModules &&
                col < offset + codeSize + quietModules;
            if (insideQuietRing) {
                cells[row][col] = 'empty';
                continue;
            }
            const distance = Math.hypot(row - centre, col - centre);
            if (distance > radius - 0.5) {
                cells[row][col] = 'empty';
                continue;
            }
            cells[row][col] = random() < exports.CIRCULAR_FILL_DENSITY ? 'fake' : 'empty';
        }
    }
    return { cells, size, codeSize, offset, quietModules };
}
exports.buildCircularGrid = buildCircularGrid;
function isDark(grid, row, col) {
    if (row < 0 || col < 0 || row >= grid.size || col >= grid.size)
        return false;
    const cell = grid.cells[row][col];
    return cell === 'real' || cell === 'fake';
}
/** One module as a path with per-corner radii, rounded only on exposed corners. */
function modulePath(grid, row, col, x, y, moduleSize, radius) {
    const up = isDark(grid, row - 1, col);
    const down = isDark(grid, row + 1, col);
    const left = isDark(grid, row, col - 1);
    const right = isDark(grid, row, col + 1);
    const topLeft = !up && !left ? radius : 0;
    const topRight = !up && !right ? radius : 0;
    const bottomRight = !down && !right ? radius : 0;
    const bottomLeft = !down && !left ? radius : 0;
    const corner = (r, endX, endY) => r > 0
        ? `A${r.toFixed(2)},${r.toFixed(2)} 0 0 1 ${endX.toFixed(2)},${endY.toFixed(2)}`
        : `L${endX.toFixed(2)},${endY.toFixed(2)}`;
    const right_ = x + moduleSize;
    const bottom = y + moduleSize;
    return (`M${(x + topLeft).toFixed(2)},${y.toFixed(2)} ` +
        `L${(right_ - topRight).toFixed(2)},${y.toFixed(2)} ${corner(topRight, right_, y + topRight)} ` +
        `L${right_.toFixed(2)},${(bottom - bottomRight).toFixed(2)} ${corner(bottomRight, right_ - bottomRight, bottom)} ` +
        `L${(x + bottomLeft).toFixed(2)},${bottom.toFixed(2)} ${corner(bottomLeft, x, bottom - bottomLeft)} ` +
        `L${x.toFixed(2)},${(y + topLeft).toFixed(2)} ${corner(topLeft, x + topLeft, y)} Z`);
}
/**
 * Render a circular QR code to an SVG string. Pure — safe from a React render
 * path or a request handler.
 */
function renderCircularQRCodeSVG(args) {
    var _a, _b, _c;
    const { data } = args;
    const style = (_a = args.style) !== null && _a !== void 0 ? _a : {};
    const grid = buildCircularGrid(data, (0, qr_code_style_1.resolveQuietModules)(style));
    const ink = escapeAttr((_b = style.dotsColor) !== null && _b !== void 0 ? _b : qr_code_style_1.DEFAULT_QR_STYLE.dotsColor);
    const rawBackground = (_c = style.backgroundColor) !== null && _c !== void 0 ? _c : qr_code_style_1.DEFAULT_QR_STYLE.backgroundColor;
    const transparent = rawBackground === qr_code_style_1.TRANSPARENT_BACKGROUND;
    const background = escapeAttr(rawBackground);
    const hasOutline = !!style.circularOutline;
    /**
     * The grid needs at least one pixel per module, and the outline needs room
     * outside it. `DownloadQRCodePayload` permits a size as small as 1, at which
     * point the module size floors to 1px, the grid is wider than the canvas and
     * every path coordinate goes negative — a blank crop delivered as a
     * successful download. Grow the canvas to whatever the grid actually needs
     * rather than emitting a broken document.
     */
    const outlineAllowance = hasOutline ? 0.098 : 0; // 2 * (0.014 + 0.035)
    const minimumSize = Math.ceil(grid.size / (1 - outlineAllowance));
    const size = Math.max(args.size, minimumSize);
    /* An outline needs room to sit outside the grid without touching it. */
    const outlineWidth = hasOutline ? Math.max(2, size * 0.014) : 0;
    const outlineGap = hasOutline ? size * 0.035 : 0;
    const available = size - 2 * (outlineWidth + outlineGap);
    /* Integer module size — see the note at the top of this file. */
    const moduleSize = Math.max(1, Math.floor(available / grid.size));
    const gridPixels = moduleSize * grid.size;
    const pad = Math.max(0, Math.round((size - gridPixels) / 2));
    const paths = [];
    for (let row = 0; row < grid.size; row++) {
        for (let col = 0; col < grid.size; col++) {
            if (!isDark(grid, row, col))
                continue;
            paths.push(modulePath(grid, row, col, pad + col * moduleSize, pad + row * moduleSize, moduleSize, moduleSize * exports.CIRCULAR_CORNER_RADIUS));
        }
    }
    const centre = size / 2;
    const outline = style.circularOutline
        ? `\n  <circle cx="${centre}" cy="${centre}" r="${(centre - outlineWidth / 2).toFixed(2)}" ` +
            `fill="none" stroke="${style.circularOutlineColor ? escapeAttr(style.circularOutlineColor) : ink}" stroke-width="${outlineWidth.toFixed(2)}"/>`
        : '';
    return (`<?xml version="1.0" standalone="no"?>\n` +
        `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">\n` +
        (transparent
            ? ''
            : `  <rect width="${size}" height="${size}" fill="${background}"/>\n`) +
        `  <path d="${paths.join(' ')}" fill="${ink}"/>` +
        `${outline}\n` +
        `</svg>`);
}
exports.renderCircularQRCodeSVG = renderCircularQRCodeSVG;
